import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import styled, { keyframes } from 'styled-components';
import { useParams, Link } from 'react-router-dom';
import blogPosts from '../data/blogPosts';
import { useThreadsAuth } from '../context/ThreadsAuthContext';
import ThreadsLoginButton from '../components/ThreadsLoginButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

// ─── Page Wrapper ────────────────────────────────────────────────────────────

const PageWrapper = styled.div`
  min-height: 100vh;
  background: #111;
  padding-bottom: 100px;
`;

// ─── Hero ─────────────────────────────────────────────────────────────────────

const Hero = styled.div`
  position: relative;
  width: 100%;
  height: 420px;
  overflow: hidden;

  @media (max-width: 768px) {
    height: 260px;
  }
`;

const HeroImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const HeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.3) 0%,
    rgba(17, 17, 17, 0.95) 100%
  );
`;

// ─── Article Layout ───────────────────────────────────────────────────────────

const Article = styled.article`
  max-width: 760px;
  margin: 0 auto;
  padding: 0 24px;
`;

// ─── Back Link ────────────────────────────────────────────────────────────────

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.85rem;
  font-weight: 600;
  color: #fe88f1;
  text-decoration: none;
  margin-top: 40px;
  margin-bottom: 32px;
  transition: gap 0.2s ease;

  &::before {
    content: '←';
  }

  &:hover {
    gap: 10px;
  }
`;

// ─── Meta Header ──────────────────────────────────────────────────────────────

const CategoryBadge = styled.span`
  display: inline-block;
  background: #fe88f1;
  color: #111;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  padding: 4px 10px;
  border-radius: 4px;
  margin-bottom: 18px;
`;

const PostTitle = styled.h1`
  font-size: 2.4rem;
  font-weight: 800;
  color: #fff;
  line-height: 1.25;
  margin: 0 0 20px;
  letter-spacing: -0.5px;

  @media (max-width: 768px) {
    font-size: 1.7rem;
  }
`;

const PostMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.45);
  padding-bottom: 28px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  margin-bottom: 36px;

  span {
    &:not(:last-child)::after {
      content: '·';
      margin-left: 12px;
    }
  }
`;

// ─── Post Content ─────────────────────────────────────────────────────────────

const PostContent = styled.div`
  font-size: 1.05rem;
  line-height: 1.8;
  color: rgba(255, 255, 255, 0.78);

  h2 {
    font-size: 1.45rem;
    font-weight: 700;
    color: #fff;
    margin: 40px 0 16px;
    padding-bottom: 8px;
    border-bottom: 2px solid rgba(254, 136, 241, 0.3);
  }

  p {
    margin: 0 0 22px;
    color: rgba(255, 255, 255, 0.72);
  }

  strong {
    color: #fe88f1;
  }

  a {
    color: #fe88f1;
    text-decoration: underline;
  }
`;

// ─── Divider ──────────────────────────────────────────────────────────────────

const Divider = styled.hr`
  border: none;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  margin: 48px 0;
`;

// ─── Comments Section ─────────────────────────────────────────────────────────

const CommentsSection = styled.section`
  margin-top: 48px;
`;

const SectionTitle = styled.h2`
  font-size: 1.3rem;
  font-weight: 700;
  color: #fff;
  margin: 0 0 28px;
  display: flex;
  align-items: center;
  gap: 10px;

  span {
    font-size: 0.85rem;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.35);
    background: rgba(255, 255, 255, 0.06);
    padding: 2px 10px;
    border-radius: 99px;
  }
`;

// Login prompt
const LoginPrompt = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  padding: 24px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  margin-bottom: 32px;
`;

const LoginPromptText = styled.p`
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.5);
  margin: 0;
`;

// Comment form
const CommentForm = styled.form`
  margin-bottom: 40px;
`;

const UserRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 14px;
`;

const Textarea = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: 14px 16px;
  background: rgba(255, 255, 255, 0.05);
  border: 1.5px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  color: #fff;
  font-size: 0.95rem;
  font-family: inherit;
  line-height: 1.6;
  resize: vertical;
  transition: border-color 0.2s;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: rgba(254, 136, 241, 0.5);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.25);
  }
`;

const FormFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 10px;
`;

const CharCount = styled.span`
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.3);
`;

const SubmitBtn = styled.button`
  padding: 10px 24px;
  background: #fe88f1;
  color: #111;
  border: none;
  border-radius: 8px;
  font-weight: 700;
  font-size: 0.9rem;
  cursor: pointer;
  transition: opacity 0.2s, transform 0.15s;

  &:hover:not(:disabled) {
    opacity: 0.88;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const SuccessMsg = styled.p`
  font-size: 0.85rem;
  color: #7effb2;
  margin: 8px 0 0;
`;

const ErrorMsg = styled.p`
  font-size: 0.85rem;
  color: #ff7e7e;
  margin: 8px 0 0;
`;

// Comment list
const spin = keyframes`to { transform: rotate(360deg); }`;

const Spinner = styled.div`
  width: 28px;
  height: 28px;
  border: 2px solid rgba(254, 136, 241, 0.15);
  border-top-color: #fe88f1;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
  margin: 24px auto;
`;

const CommentList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const CommentItem = styled.li`
  padding: 20px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 10px;
`;

const CommentHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
`;

const CommentAvatar = styled.img`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid rgba(255, 255, 255, 0.1);
`;

const CommentAvatarFallback = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #2a2a2a;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  font-weight: 700;
  color: #fe88f1;
  flex-shrink: 0;
`;

const CommentMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const CommentAuthor = styled.span`
  font-size: 0.9rem;
  font-weight: 700;
  color: #fff;
`;

const CommentDate = styled.span`
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.35);
`;

const CommentText = styled.p`
  font-size: 0.95rem;
  line-height: 1.65;
  color: rgba(255, 255, 255, 0.72);
  margin: 0;
  white-space: pre-wrap;
`;

const EmptyComments = styled.p`
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.3);
  text-align: center;
  padding: 32px 0;
`;

// ─── Not Found ────────────────────────────────────────────────────────────────

const NotFoundWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  text-align: center;
  padding: 40px 24px;

  h2 { font-size: 1.8rem; color: #fff; margin-bottom: 16px; }
  p  { color: rgba(255, 255, 255, 0.5); margin-bottom: 32px; }
`;

const BackBtn = styled(Link)`
  display: inline-block;
  padding: 12px 28px;
  background: #fe88f1;
  color: #111;
  font-weight: 700;
  font-size: 0.9rem;
  border-radius: 6px;
  text-decoration: none;
  transition: opacity 0.2s ease;

  &:hover { opacity: 0.85; }
`;

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric',
    });
  } catch {
    return '';
  }
};

// ─── Comments Sub-Component ───────────────────────────────────────────────────

const Comments = ({ postSlug }) => {
  const { isLoggedIn, token } = useThreadsAuth();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const fetchComments = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${BACKEND_URL}/comments/${postSlug}`);
      if (!res.ok) throw new Error('Failed to load comments');
      const data = await res.json();
      setComments(data);
    } catch {
      // silently fail — comments just won't show
    } finally {
      setLoading(false);
    }
  }, [postSlug]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!text.trim() || !token) return;

    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const res = await fetch(`${BACKEND_URL}/comments/${postSlug}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ text: text.trim() }),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to post comment');
      }

      setText('');
      setSuccess('Comment posted!');
      fetchComments();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <CommentsSection>
      <SectionTitle>
        Comments <span>{comments.length}</span>
      </SectionTitle>

      {/* Login prompt or comment form */}
      {!isLoggedIn ? (
        <LoginPrompt>
          <LoginPromptText>
            Sign in with your Threads account to join the conversation.
          </LoginPromptText>
          <ThreadsLoginButton returnTo={`/blogs/${postSlug}`} />
        </LoginPrompt>
      ) : (
        <CommentForm onSubmit={handleSubmit}>
          <UserRow>
            <ThreadsLoginButton />
          </UserRow>
          <Textarea
            value={text}
            onChange={(e) => {
              if (e.target.value.length <= 1000) setText(e.target.value);
            }}
            placeholder="Share your thoughts on this article…"
            rows={4}
          />
          <FormFooter>
            <CharCount>{text.length}/1000</CharCount>
            <SubmitBtn type="submit" disabled={submitting || !text.trim()}>
              {submitting ? 'Posting…' : 'Post Comment'}
            </SubmitBtn>
          </FormFooter>
          {success && <SuccessMsg>{success}</SuccessMsg>}
          {error && <ErrorMsg>{error}</ErrorMsg>}
        </CommentForm>
      )}

      {/* Comment list */}
      {loading ? (
        <Spinner />
      ) : comments.length === 0 ? (
        <EmptyComments>No comments yet. Be the first to start the conversation!</EmptyComments>
      ) : (
        <CommentList>
          {comments.map((c) => (
            <CommentItem key={c._id}>
              <CommentHeader>
                {c.author?.profilePicUrl ? (
                  <CommentAvatar
                    src={c.author.profilePicUrl}
                    alt={c.author.name || c.author.username}
                  />
                ) : (
                  <CommentAvatarFallback>
                    {(c.author?.name || c.author?.username || '?')[0].toUpperCase()}
                  </CommentAvatarFallback>
                )}
                <CommentMeta>
                  <CommentAuthor>
                    @{c.author?.username || c.author?.name || 'Anonymous'}
                  </CommentAuthor>
                  <CommentDate>{formatDate(c.createdAt)}</CommentDate>
                </CommentMeta>
              </CommentHeader>
              <CommentText>{c.text}</CommentText>
            </CommentItem>
          ))}
        </CommentList>
      )}
    </CommentsSection>
  );
};

// ─── Main BlogPost Component ───────────────────────────────────────────────────

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetch(`${BACKEND_URL}/posts/${slug}`)
      .then((r) => {
        if (r.ok) return r.json();
        throw new Error('not found');
      })
      .then((data) => { if (!cancelled) { setPost(data); setLoading(false); } })
      .catch(() => {
        // Fall back to static posts
        const staticPost = blogPosts.find((p) => p.slug === slug) || null;
        if (!cancelled) { setPost(staticPost); setLoading(false); }
      });
    return () => { cancelled = true; };
  }, [slug]);

  if (loading) {
    return (
      <PageWrapper>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '120px 0' }}>
          <Spinner />
        </div>
      </PageWrapper>
    );
  }

  if (!post) {
    return (
      <PageWrapper>
        <NotFoundWrapper>
          <h2>Post Not Found</h2>
          <p>The article you're looking for doesn't exist or has been moved.</p>
          <BackBtn to="/blogs">← Back to Blog</BackBtn>
        </NotFoundWrapper>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <Helmet>
        <title>{post.title} — MyCinemaDB Blog</title>
        <meta name="description" content={post.excerpt || `${post.title} — Read this article on the MyCinemaDB cinema blog.`} />
      </Helmet>
      <Hero>
        <HeroImage src={post.coverImage} alt={post.title} />
        <HeroOverlay />
      </Hero>

      <Article>
        <BackLink to="/blogs">Back to Blog</BackLink>

        <CategoryBadge>{post.category}</CategoryBadge>
        <PostTitle>{post.title}</PostTitle>

        <PostMeta>
          <span>{post.author}</span>
          <span>{post.date}</span>
        </PostMeta>

        <PostContent dangerouslySetInnerHTML={{ __html: post.content }} />

        <Divider />

        <Comments postSlug={slug} />

        <Divider />

        <BackLink to="/blogs">Back to Blog</BackLink>
      </Article>
    </PageWrapper>
  );
};

export default BlogPost;
